import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import AuditTable from '../auditlogtable/auditTable';
import { getAudits } from '../../api/audits';
import { getUsersForAccount } from '../../api/users';
import styles from '../../styles/archiveui.module.css';
import { withRouter } from 'react-router';
import { Loading } from '../../Loading';
import { AuditEntry } from '../../types/audit';
import { User } from '../../types/user';
import { Redirect } from 'react-router-dom';
import { userHasAccess } from '../../users/userprofile/user';
import { useUserContext } from '../../contexts/UserContext';
import { downloadCsv } from '../../utils/fileutils';

const AuditLog = (): JSX.Element | null => {
  const hasAccess = userHasAccess([{ permissionId: 'auditlog', action: 'read' }]);
  const { accountId } = useUserContext().currentAccount;
  const [audits, updateAudits] = useState<AuditEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [actorFilter, updateActorFilter] = useState<string>('');
  const [targetFilter, updateTargetFilter] = useState<string>('');
  const [actionFilter, updateActionFilter] = useState<string>('');
  const [startDate, updateStartDate] = useState<string>('');
  const [endDate, updateEndDate] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const fetchAudits = async () => {
    const results = await getAudits(accountId, actorFilter, targetFilter, actionFilter, startDate, endDate);
    updateAudits(results);
  };

  const sanitizeCsvCell = (value: string | undefined | null): string => {
    const str = value ?? '';
    return /^[=+\-@\t\r]/.test(str) ? ` ${str}` : str;
  };

  const exportAudits = async () => {
    try {
      const all = await getAudits(accountId, actorFilter, targetFilter, actionFilter, startDate, endDate, 10000);
      const csv = Papa.unparse(
        all.map((entry) => {
          const actorUser = users.find((u) => u.cognitoIds?.includes(entry.actor));
          const actorName = actorUser ? [actorUser.givenName, actorUser.familyName].filter(Boolean).join(' ') || actorUser.email : entry.actor;
          return {
            'Document ID': sanitizeCsvCell(entry.target ? entry.target.split('/').filter(Boolean).at(-1) ?? entry.target : ''),
            Time: entry.time ? new Date(entry.time).toISOString().replace('T', ' ').slice(0, 19) : '',
            accountId: entry.accountId,
            Application: entry.application,
            Actor: actorName,
            SourceType: entry.sourceType,
            Source: entry.source,
            SourceIP: entry.sourceip,
            Action: entry.action,
            Description: sanitizeCsvCell(entry.description),
            Result: entry.result,
            Meta: sanitizeCsvCell(entry.meta),
          };
        }),
      );
      downloadCsv(`audit-log-${accountId}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    } catch (e) {
      console.error('Failed to export audit log CSV', e);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        await Promise.all([
          fetchAudits(),
          getUsersForAccount(accountId)
            .then((loadedUsers) => {
              if (isMounted) setUsers(loadedUsers);
            })
            .catch((err) => {
              console.error('Failed to load users for audit filter:', err);
              if (isMounted) setUsers([]);
            }),
        ]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    void loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  if (!hasAccess) {
    return <Redirect to="/notauthorized" />;
  }

  return (
    <div className={styles.customLogContainer}>
      {loading && <Loading />}
      {!loading && (
        <AuditTable
          audits={audits}
          users={users}
          exportAudits={exportAudits}
          updateActorFilter={updateActorFilter}
          updateTargetFilter={updateTargetFilter}
          updateActionFilter={updateActionFilter}
          updateStartDate={updateStartDate}
          updateEndDate={updateEndDate}
          actorFilter={actorFilter}
          targetFilter={targetFilter}
          actionFilter={actionFilter}
          startDate={startDate}
          endDate={endDate}
          searchAudits={fetchAudits}
        />
      )}
    </div>
  );
};

export default withRouter(AuditLog);
